import "./Exhibition.css";

import {useClientLocale} from "../../context/client_locale";
import {useAdaptiveMobileSize} from "../../hooks/useAdaptiveMobileSize";
import {useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";

import PlainControl from "../../components/plainControl/PlainControl";
import {ErrorMessage} from "../../components/errorMessage/ErrorMessage";
import LoadingState from "../../components/loadingState/LoadingState";

import {cacheImages, fileInfo} from "../../utils/file";
import {getActualResolutionCategory, getWindowSize} from "../../utils/size";
import {remoteFilePath} from "../../services/api/File";
import {fetchExhibitionByID} from "../../services/api/Exhibition";
import {getStyleForClientRootContainer} from "../../helper/client_block_style";

export default function Exhibition() {
    const { strings } = useClientLocale();
    const [searchParams] = useSearchParams();
    const id = parseInt(searchParams.get('id'));
    const mobileVersionParameter = searchParams.get('mobile_version');
    let isMobileVersion = false;
    if (mobileVersionParameter) {
      isMobileVersion = mobileVersionParameter === "true";
    }

    const navigate = useNavigate();
    const adaptiveMobileSize = useAdaptiveMobileSize();
    const resolutionUnit = getActualResolutionCategory(
        isMobileVersion ? adaptiveMobileSize : getWindowSize()
    )
    const [exhibition, setExhibition] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(function () {
        setIsLoading(true);
        fetchExhibitionByID(id)
            .then( (res) => {
                const exhibitionResponse = res.data["response"]
                let exhibition = {}
                exhibition.id = exhibitionResponse["id"]
                exhibition.clientCode = exhibitionResponse["client_code"]
                exhibition.productCode = exhibitionResponse["product_code"]
                const fileInfos = exhibitionResponse.files.map((item) => {
                    return fileInfo(item.id, item.path, item.title, item.tag, item.priority, null)
                })
                exhibition.backgroundImages = fileInfos.reduce((images, fileInfo) => {
                    if (fileInfo.tag === 'image' && fileInfo.priority === 0) {
                        images[fileInfo.title] = fileInfo.id;
                    }
                    return images;
                }, {});

                exhibition.images = fileInfos.filter(fileInfo => {
                    return fileInfo.tag === 'image' && fileInfo.priority > 0
                });
                exhibition.audios = fileInfos.filter(fileInfo => {
                    return fileInfo.tag === 'audio';
                });
                exhibition.videos = fileInfos.filter(fileInfo=> {
                    return fileInfo.tag === 'video';
                });
                exhibition.mediaOrder = [...exhibition.audios, ...exhibition.videos, ...exhibition.images]
                    .filter(value => value.priority > 0)
                    .reduce((mediaOrder, fileInfo) => {
                        mediaOrder[fileInfo.id] = fileInfo.priority;
                        return mediaOrder;
                }, {});
                return exhibition
            }).then( exhibition => {
                let imagePaths = [
                    ...Object.values(exhibition.backgroundImages),
                    ...exhibition.images.map(image => image.id),
                ].map((imageID) => {
                    return remoteFilePath(imageID)
                })
                return cacheImages(imagePaths).then(() => {
                    return exhibition
                })
            }).then((exhibition) => {
                setExhibition(exhibition)
                setIsLoading(false)
            }).catch( err => {
                console.log(err);
                if (err?.response?.data?.["error_message"]) {
                    const errMessage = err?.response?.data?.["error_message"]
                    setErrorMessage(errMessage)
                }
                setIsLoading(false)
            });
    }, [])

    let backgroundURL = null
    let mainContent
    if (isLoading) {
        mainContent = (
            <div className={"h-dvh flex flex-col justify-center items-center"}>
                <LoadingState
                    resolutionUnit={resolutionUnit}
                    text={"Loading ..."}
                />
            </div>
        )
    } else if (exhibition) {
        const titleID = exhibition.backgroundImages["Title"]
        backgroundURL = remoteFilePath(titleID)
        function nextHandle() {
            navigate("/welcome", {
                state: {
                    exhibition: exhibition,
                    isMobileVersion: isMobileVersion,
                }
            })
        }
        mainContent = (
            <div className={"h-full w-full flex flex-col"}>
                <div className={"basis-3/4 grow"}></div>
                <div className={"basis-1/4 grow"}>
                    <PlainControl
                        onClick={nextHandle}
                        resolutionUnit={resolutionUnit}
                    >
                        {strings["next"].toUpperCase()}
                    </PlainControl>
                </div>
            </div>
        )
    } else if (errorMessage) {
        mainContent = (
            <div className={"h-full w-full flex flex-col justify-center"}>
                <ErrorMessage>{errorMessage}</ErrorMessage>
            </div>
        )
    } else {
        mainContent = (
            <div className={"h-full w-full flex flex-col justify-center"}>
                <ErrorMessage>{strings["exhibition_not_exists"]}</ErrorMessage>
            </div>
        )
    }
    const containerStyle = getStyleForClientRootContainer(backgroundURL, adaptiveMobileSize, isMobileVersion);
    return (
        <div className="flex flex-row justify-center">
          <div className="bg-center bg-no-repeat" style={containerStyle}>
              {mainContent}
          </div>
        </div>
    )
}
